import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SelectBox } from "devextreme-react/select-box";
import { FilterTabs, MasterPage, ViewOnlyNote } from "../Shared/master";
import useLookup from "../../hooks/useLookup";
import useAbortSignal from "../../hooks/useAbortSignal";
import { isRequestCancelled } from "../../apis/useAxios";
import { showErrorToast } from "../../helpers/message";
import { usePermission } from "../../helpers/permissions";
import { getAirportDropdown } from "../nonflyActivityLocation/activityLocationService";
import { CREW_TYPES, MENU_CODE, STATUS_FILTERS, filterCards, groupByAirport } from "./hotelPriorityRules";
import { getPriorities } from "./hotelPriorityService";
import PriorityCard from "./PriorityCard";
import AddPriorityHotel from "./AddPriorityHotel";
import "./hotelPriority.css";

/**
 * Configuration › Booking Hotel Priority: per airport the cockpit and cabin crew hotels in booking order. When a hotel
 * rejects a booking, the booking engine moves the crew to the next hotel of the list. Edit rights: add, reorder, remove.
 */
export function HotelBookingPriority() {
  const getSignal = useAbortSignal();
  const { edit: canEdit } = usePermission(MENU_CODE);
  const airline = sessionStorage.getItem("session_airlineCode") ?? "";
  const { items: airports } = useLookup(`airports:${airline}`, getAirportDropdown);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("set");
  const [airportCode, setAirportCode] = useState("");
  const [adding, setAdding] = useState(null); // null = closed, "" or an airport code = open

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await getPriorities("", { signal: getSignal() }));
    } catch (err) {
      if (!isRequestCancelled(err)) showErrorToast(err?.message);
    } finally {
      setLoading(false);
    }
  }, [getSignal]);

  useEffect(() => {
    load();
  }, [load]);

  const cards = useMemo(() => groupByAirport(rows, airports), [rows, airports]);
  const counts = useMemo(() => Object.fromEntries(STATUS_FILTERS.map((f) => [f.id, filterCards(cards, f.id).length])), [cards]);
  const shown = useMemo(() => (airportCode ? cards.filter((c) => c.airportCode === airportCode) : filterCards(cards, status)), [cards, status, airportCode]);

  return (
    <MasterPage title="Booking Hotel Priority">
      <div className="hbp-page">
        <FilterTabs
          ariaLabel="Filter airports"
          items={STATUS_FILTERS.map((f) => ({ ...f, count: counts[f.id] }))}
          value={status}
          total={counts.all}
          onChange={setStatus}
        />
        <section className="hbp-toolbar" aria-label="Toolbar">
          <div className="hbp-toolbar__airport">
            <SelectBox
              dataSource={airports}
              displayExpr="airportName"
              valueExpr="airportCode"
              value={airportCode || null}
              placeholder="All airports"
              showClearButton
              searchEnabled
              inputAttr={{ "aria-label": "Airport" }}
              onValueChanged={(e) => setAirportCode(e.value ?? "")}
            />
          </div>
          <p className="hbp-hint">
            <i className="fas fa-info-circle" aria-hidden="true" /> When a hotel rejects a booking, the crew moves to the next hotel of the list (1 = first
            choice).
          </p>
          <button type="button" className="btn btn-outline-primary btn-sm" disabled={loading} title="Reload" aria-label="Reload" onClick={load}>
            <i className={`fas fa-sync-alt${loading ? " fa-spin" : ""}`} aria-hidden="true" />
          </button>
          {canEdit && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(airportCode)}>
              <i className="fas fa-plus" aria-hidden="true" /> Add hotel
            </button>
          )}
        </section>
        {!canEdit && <ViewOnlyNote />}

        {loading && !rows.length ? (
          <p className="hbp-empty">Loading…</p>
        ) : shown.length ? (
          <div className="hbp-cards">
            {shown.map((c) => (
              <PriorityCard key={c.airportCode} card={c} crewTypes={CREW_TYPES} canEdit={canEdit} onAdd={setAdding} onChanged={load} />
            ))}
          </div>
        ) : (
          <p className="hbp-empty">{status === "set" && !airportCode ? "No airport has a hotel priority yet." : "No airport to show."}</p>
        )}
      </div>
      {adding !== null && <AddPriorityHotel airportCode={adding} airports={airports} onSaved={load} onClose={() => setAdding(null)} />}
    </MasterPage>
  );
}

export default HotelBookingPriority;
